import { useState, useEffect } from 'react';
import { Dialog, DialogPanel, Title, Text, TextInput, Button, Select, SelectItem } from '@tremor/react';
import { api } from '../../lib/api';
import type {
  ClassifierBackend,
  ClassifierMode,
  CreateVersionPayload,
  ParserType,
} from '../../types';

interface NewVersionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  classifierKey: string;
  classifierName: string;
  existingVersionCount: number;
}

const PARSER_TYPES: { value: ParserType; label: string; hint: string }[] = [
  { value: 'yes-no', label: 'Yes / No', hint: 'Model says "yes" → first non-neutral label, otherwise neutral.' },
  { value: 'label-match', label: 'Label match', hint: 'Looks for any label name inside the response.' },
  { value: 'json-array', label: 'JSON array', hint: 'Response is parsed as JSON array of label strings, e.g. ["intimidation"].' },
  { value: 'json-object', label: 'JSON object', hint: 'Response is parsed as JSON, reads a named field (default: "label").' },
  { value: 'regex-tag', label: 'Regex tag', hint: 'Extracts label from a tag pattern (e.g. <label>X</label>).' },
];

const DEFAULT_USER_TEMPLATE = `Decide whether the following text contains any occurrences of the target category.

Text:
{{text}}

Reply with one of: {{labels}}.`;

export function NewVersionModal({ isOpen, onClose, onSuccess, classifierKey, classifierName, existingVersionCount }: NewVersionModalProps) {
  const [version, setVersion] = useState('');
  const [mode, setMode] = useState<ClassifierMode>('fine-tuned');
  const [backend, setBackend] = useState<ClassifierBackend>('openai');
  const [modelId, setModelId] = useState('');
  const [labelsText, setLabelsText] = useState('');
  const [logitBiasText, setLogitBiasText] = useState('');
  const [systemPrompt, setSystemPrompt] = useState('');
  const [userPromptTemplate, setUserPromptTemplate] = useState(DEFAULT_USER_TEMPLATE);
  const [temperature, setTemperature] = useState('0');
  const [maxOutputTokens, setMaxOutputTokens] = useState('64');
  const [maxInputTokens, setMaxInputTokens] = useState('');
  const [parserType, setParserType] = useState<ParserType>('yes-no');
  const [parserConfigText, setParserConfigText] = useState('{}');
  const [notes, setNotes] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      setVersion(String(existingVersionCount + 1));
      setMode('fine-tuned');
      setBackend('openai');
      setModelId('');
      setLabelsText('');
      setLogitBiasText('');
      setSystemPrompt('');
      setUserPromptTemplate(DEFAULT_USER_TEMPLATE);
      setTemperature('0');
      setMaxOutputTokens('64');
      setMaxInputTokens('');
      setParserType('yes-no');
      setParserConfigText('{}');
      setNotes('');
      setError(null);
    }
  }, [isOpen, existingVersionCount]);

  // Anthropic only supports prompt mode; flip mode for the user automatically.
  useEffect(() => {
    if (backend === 'anthropic' && mode !== 'prompt') {
      setMode('prompt');
    }
  }, [backend, mode]);

  const isPromptMode = mode === 'prompt';
  const modelLabel = backend === 'openai'
    ? (isPromptMode ? 'OpenAI Model (e.g. gpt-4o-mini)' : 'OpenAI Fine-Tuned Model ID')
    : backend === 'anthropic'
      ? 'Anthropic Model (e.g. claude-haiku-4-5-20251001)'
      : (isPromptMode ? 'vLLM Instruct Model (e.g. phi-3-instruct)' : 'vLLM Model / Adapter Name');
  const modelPlaceholder = backend === 'openai'
    ? (isPromptMode ? 'gpt-4o-mini' : 'ft:gpt-4o-mini-2024-07-18:alphy-co:...')
    : backend === 'anthropic'
      ? 'claude-haiku-4-5-20251001'
      : (isPromptMode ? 'phi-3-instruct' : 'phi-sentiment');

  const handleSubmit = async () => {
    if (!version.trim() || !modelId.trim() || !labelsText.trim()) {
      setError('Version, model, and labels are required');
      return;
    }

    const labels = labelsText.split(/[,\n]/).map(l => l.trim()).filter(Boolean);
    if (labels.length === 0) {
      setError('At least one label is required');
      return;
    }

    let logitBias: Record<string, number> | undefined;
    if (!isPromptMode && backend === 'openai' && logitBiasText.trim()) {
      try {
        logitBias = JSON.parse(logitBiasText);
      } catch {
        setError('Logit bias must be valid JSON (e.g., {"15": 100, "16": 100})');
        return;
      }
    }

    let parserConfig: Record<string, unknown> = {};
    if (isPromptMode && parserConfigText.trim()) {
      try {
        parserConfig = JSON.parse(parserConfigText);
      } catch {
        setError('Parser config must be valid JSON (or "{}").');
        return;
      }
    }

    if (isPromptMode && !userPromptTemplate.trim()) {
      setError('User prompt template is required for prompt mode');
      return;
    }

    const payload: CreateVersionPayload = {
      version: version.trim(),
      backend,
      mode,
      labels,
      notes: notes.trim() || undefined,
    };

    if (backend === 'openai') {
      payload.openai = { model: modelId.trim(), ...(logitBias ? { logitBias } : {}) };
    } else if (backend === 'phi') {
      payload.phi = { model: modelId.trim() };
    } else if (backend === 'anthropic') {
      payload.anthropic = { model: modelId.trim() };
    }

    if (isPromptMode) {
      payload.prompt = {
        systemPrompt: systemPrompt.trim() || undefined,
        userPromptTemplate: userPromptTemplate,
        temperature: Number(temperature) || 0,
        maxOutputTokens: Number(maxOutputTokens) || 64,
        ...(maxInputTokens.trim() ? { maxInputTokens: Number(maxInputTokens) } : {}),
      };
      payload.parser = { type: parserType, config: parserConfig };
    }

    setIsLoading(true);
    setError(null);
    try {
      await api.createVersion(classifierKey, payload);
      onSuccess();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create version');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onClose={onClose} static={true}>
      <DialogPanel className="bg-gray-900 border border-gray-800 max-w-2xl">
        <Title className="text-white">New Version for {classifierName}</Title>
        <Text className="text-gray-400 mt-1">
          Register a new model version. Model config is immutable after creation.
        </Text>

        <div className="mt-4 space-y-4">
          <div>
            <label className="text-xs text-gray-400 uppercase tracking-wide">Version Identifier</label>
            <TextInput
              placeholder="e.g., openai-2026-04-22-abc, intimidation-llm-v1"
              value={version}
              onChange={(e) => setVersion(e.target.value)}
              className="mt-1"
            />
            <p className="text-xs text-gray-500 mt-1">Free-form name for this version. Must be unique within this classifier.</p>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-gray-400 uppercase tracking-wide">Mode</label>
              <Select value={mode} onValueChange={(v) => setMode(v as ClassifierMode)} className="mt-1">
                <SelectItem value="fine-tuned">Fine-tuned model</SelectItem>
                <SelectItem value="prompt">Prompt-based LLM</SelectItem>
              </Select>
              <p className="text-xs text-gray-500 mt-1">
                Prompt mode lets you author a classifier from a template + parser, no fine-tune required.
              </p>
            </div>
            <div>
              <label className="text-xs text-gray-400 uppercase tracking-wide">Backend</label>
              <Select value={backend} onValueChange={(v) => setBackend(v as ClassifierBackend)} className="mt-1">
                <SelectItem value="openai">OpenAI</SelectItem>
                <SelectItem value="phi">Phi / vLLM</SelectItem>
                <SelectItem value="anthropic">Anthropic / Claude</SelectItem>
              </Select>
              {backend === 'anthropic' && (
                <p className="text-xs text-amber-400 mt-1">Anthropic only supports prompt mode.</p>
              )}
            </div>
          </div>

          <div>
            <label className="text-xs text-gray-400 uppercase tracking-wide">{modelLabel}</label>
            <TextInput
              placeholder={modelPlaceholder}
              value={modelId}
              onChange={(e) => setModelId(e.target.value)}
              className="mt-1"
            />
          </div>

          <div>
            <label className="text-xs text-gray-400 uppercase tracking-wide">Labels (comma or newline separated)</label>
            <textarea
              className="mt-1 w-full rounded border border-gray-700 bg-gray-800 text-gray-200 text-sm px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 min-h-[80px]"
              placeholder="neutral, intimidation"
              value={labelsText}
              onChange={(e) => setLabelsText(e.target.value)}
            />
            <p className="text-xs text-gray-500 mt-1">First label should be 'neutral' by convention.</p>
          </div>

          {!isPromptMode && backend === 'openai' && (
            <div>
              <label className="text-xs text-gray-400 uppercase tracking-wide">Logit Bias (optional, JSON)</label>
              <textarea
                className="mt-1 w-full rounded border border-gray-700 bg-gray-800 text-gray-200 text-sm px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono min-h-[60px]"
                placeholder='{"15": 100, "16": 100}'
                value={logitBiasText}
                onChange={(e) => setLogitBiasText(e.target.value)}
              />
              <p className="text-xs text-gray-500 mt-1">Token IDs that constrain the fine-tuned model to valid label indices.</p>
            </div>
          )}

          {isPromptMode && (
            <>
              <div>
                <label className="text-xs text-gray-400 uppercase tracking-wide">System Prompt (optional)</label>
                <textarea
                  className="mt-1 w-full rounded border border-gray-700 bg-gray-800 text-gray-200 text-sm px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 min-h-[60px]"
                  placeholder="You are a content moderation assistant..."
                  value={systemPrompt}
                  onChange={(e) => setSystemPrompt(e.target.value)}
                />
              </div>

              <div>
                <label className="text-xs text-gray-400 uppercase tracking-wide">User Prompt Template</label>
                <textarea
                  className="mt-1 w-full rounded border border-gray-700 bg-gray-800 text-gray-200 text-sm px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono min-h-[140px]"
                  value={userPromptTemplate}
                  onChange={(e) => setUserPromptTemplate(e.target.value)}
                />
                <p className="text-xs text-gray-500 mt-1">
                  Use <code className="text-gray-300">{'{{text}}'}</code> for the input sentence and{' '}
                  <code className="text-gray-300">{'{{labels}}'}</code> for the label list.
                </p>
              </div>

              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="text-xs text-gray-400 uppercase tracking-wide">Temperature</label>
                  <TextInput value={temperature} onChange={(e) => setTemperature(e.target.value)} className="mt-1" />
                </div>
                <div>
                  <label className="text-xs text-gray-400 uppercase tracking-wide">Max output tokens</label>
                  <TextInput value={maxOutputTokens} onChange={(e) => setMaxOutputTokens(e.target.value)} className="mt-1" />
                </div>
                <div>
                  <label className="text-xs text-gray-400 uppercase tracking-wide">Max input tokens</label>
                  <TextInput
                    placeholder="optional"
                    value={maxInputTokens}
                    onChange={(e) => setMaxInputTokens(e.target.value)}
                    className="mt-1"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-gray-400 uppercase tracking-wide">Parser</label>
                  <Select value={parserType} onValueChange={(v) => setParserType(v as ParserType)} className="mt-1">
                    {PARSER_TYPES.map(p => (
                      <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                    ))}
                  </Select>
                  <p className="text-xs text-gray-500 mt-1">
                    {PARSER_TYPES.find(p => p.value === parserType)?.hint}
                  </p>
                </div>
                <div>
                  <label className="text-xs text-gray-400 uppercase tracking-wide">Parser Config (JSON)</label>
                  <textarea
                    className="mt-1 w-full rounded border border-gray-700 bg-gray-800 text-gray-200 text-sm px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono min-h-[80px]"
                    placeholder='{"multiLabel": false}'
                    value={parserConfigText}
                    onChange={(e) => setParserConfigText(e.target.value)}
                  />
                </div>
              </div>
            </>
          )}

          <div>
            <label className="text-xs text-gray-400 uppercase tracking-wide">Notes (optional)</label>
            <textarea
              className="mt-1 w-full rounded border border-gray-700 bg-gray-800 text-gray-200 text-sm px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 min-h-[60px]"
              placeholder="Training details, dataset info, changes from previous version..."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          </div>

          {error && (
            <div className="bg-red-950/50 border border-red-900 rounded p-3">
              <p className="text-red-400 text-sm">{error}</p>
            </div>
          )}
        </div>

        <div className="mt-6 flex justify-end gap-3">
          <Button variant="secondary" onClick={onClose} disabled={isLoading}>Cancel</Button>
          <Button
            onClick={handleSubmit}
            loading={isLoading}
            disabled={!version.trim() || !modelId.trim() || !labelsText.trim()}
          >
            Register Version
          </Button>
        </div>
      </DialogPanel>
    </Dialog>
  );
}
